import { useMemo, useRef, useState, useEffect } from "react";
import { CaretDown, MagnifyingGlass, X } from "@phosphor-icons/react";

/**
 * MultiSelectCountries — compact multi-select with search + chips + region
 * quick-pick. Works for both 4-country lists (Family) and 197-country lists
 * (World). `regions` is optional: an object mapping region label →
 * array of countries to select on click.
 *
 * Props:
 *   value: string[] — currently selected countries (canonical names)
 *   onChange: (list: string[]) => void
 *   options: string[] — canonical country names to choose from
 *   regions?: Record<string, string[]> — optional quick-picks
 *   placeholder?: string
 *   disabled?: boolean
 *   testId?: string
 */
export default function MultiSelectCountries({
  value = [],
  onChange,
  options = [],
  regions = null,
  placeholder = "Any (auto-prioritise)",
  disabled = false,
  testId = "multi-country",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const rootRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return options;
    return options.filter((o) => o.toLowerCase().includes(qq));
  }, [q, options]);

  const toggle = (c) => {
    if (value.includes(c)) onChange(value.filter((x) => x !== c));
    else onChange([...value, c]);
  };
  const clear = () => onChange([]);
  const applyRegion = (list) => {
    // Merge without duplicates.
    const set = new Set(value);
    list.forEach((c) => set.add(c));
    onChange(Array.from(set));
  };

  const label =
    value.length === 0
      ? placeholder
      : value.length <= 2
      ? value.join(", ")
      : `${value.length} countries selected`;

  return (
    <div className="relative" ref={rootRef} data-testid={testId}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        data-testid={`${testId}-toggle`}
        className="w-full mt-1.5 flex items-center justify-between gap-2 rounded-lg border border-rf-ink/15 px-3 py-2 text-sm text-rf-ink focus:outline-none focus:border-rf-amber bg-white disabled:opacity-50"
      >
        <span className={value.length === 0 ? "text-rf-ink-soft" : ""}>{label}</span>
        <CaretDown size={14} weight="bold" className={open ? "rotate-180 transition-transform" : "transition-transform"} />
      </button>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`${testId}-chips`}>
          {value.map((c) => (
            <span
              key={c}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rf-amber/25 text-rf-ink text-xs font-bold"
            >
              {c}
              <button
                type="button"
                aria-label={`Remove ${c}`}
                onClick={() => toggle(c)}
                data-testid={`${testId}-chip-remove-${c.replace(/ /g, "-").toLowerCase()}`}
                className="hover:text-red-600"
              >
                <X size={11} weight="bold" />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={clear}
            data-testid={`${testId}-clear`}
            className="text-[11px] font-bold text-rf-ink-soft hover:text-red-600 underline underline-offset-2"
          >
            Clear all
          </button>
        </div>
      )}

      {open && (
        <div
          className="absolute z-30 mt-2 w-full min-w-[280px] max-h-[420px] rounded-xl border border-rf-ink/15 bg-white shadow-lg flex flex-col"
          data-testid={`${testId}-panel`}
        >
          <div className="p-2 border-b border-rf-ink/10">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-rf-ink-soft" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search countries…"
                data-testid={`${testId}-search`}
                className="w-full pl-8 pr-2 py-1.5 rounded-md border border-rf-ink/10 text-sm focus:outline-none focus:border-rf-amber"
                autoFocus
              />
            </div>
          </div>

          {regions && Object.keys(regions).length > 0 && (
            <div className="p-2 border-b border-rf-ink/10 flex flex-wrap gap-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wide text-rf-ink-soft self-center mr-1">Quick pick:</span>
              {Object.entries(regions).map(([regionName, list]) => (
                <button
                  key={regionName}
                  type="button"
                  onClick={() => applyRegion(list)}
                  data-testid={`${testId}-region-${regionName.replace(/ /g, "-").toLowerCase()}`}
                  className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-rf-cream text-rf-ink hover:bg-rf-amber/40 border border-rf-ink/10"
                >
                  {regionName}
                </button>
              ))}
            </div>
          )}

          <div className="overflow-y-auto flex-1 py-1">
            {filtered.length === 0 ? (
              <p className="text-xs text-rf-ink-soft px-3 py-2">No matches.</p>
            ) : (
              filtered.map((c) => {
                const active = value.includes(c);
                return (
                  <label
                    key={c}
                    data-testid={`${testId}-option-${c.replace(/ /g, "-").toLowerCase()}`}
                    className={`flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-rf-cream ${active ? "text-rf-ink font-bold" : "text-rf-ink-soft"}`}
                  >
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => toggle(c)}
                      className="w-3.5 h-3.5 accent-rf-amber"
                    />
                    {c}
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
